import { motion } from "framer-motion";
import { ArrowRight, Phone, Mail, MapPin, Calendar } from "lucide-react";
import { BOOK_URL } from "@/lib/booking";

/**
 * Booking section for the Contact page.
 * Left column: contact info cards (phone / email / coverage).
 * Right column: a bold booking panel — the "Book now" button opens the Calendly
 * booking page in a new tab via the BOOK_URL constant (see /lib/booking.js).
 */
export default function BookSection() {
  return (
    <section id="book" className="py-24 sm:py-32 border-t border-[#0B1215]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* LEFT — contact channels */}
          <div className="lg:col-span-5">
            <div className="font-mono-label text-[#FF5A36] mb-4">· BOOK DIRECTLY</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0B1215] tracking-tighter leading-[0.95]">
              Pick a slot.<br />
              <span className="italic font-light">Job done.</span>
            </h2>
            <p className="mt-6 text-[#495057] leading-relaxed max-w-md">
              Bookings run through my live diary — you'll see real availability, pick a slot
              that suits, and get an instant confirmation. For anything bespoke, just call or
              email using the details below.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href="tel:+447346812685"
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 border-2 border-[#0B1215] flex items-center justify-center group-hover:bg-[#FF5A36] group-hover:border-[#FF5A36] group-hover:text-[#F8F9FA] transition-colors">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="font-mono-label text-[#495057]">· PHONE</div>
                  <div className="font-display text-xl">07346 812 685</div>
                </div>
              </a>
              <a
                href="mailto:rob@thepropertycuratorwarwickshire.co.uk"
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 border-2 border-[#0B1215] flex items-center justify-center group-hover:bg-[#FF5A36] group-hover:border-[#FF5A36] group-hover:text-[#F8F9FA] transition-colors">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="font-mono-label text-[#495057]">· EMAIL</div>
                  <div className="font-display text-base sm:text-lg break-all">
                    rob@thepropertycuratorwarwickshire.co.uk
                  </div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 border-2 border-[#0B1215] flex items-center justify-center">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="font-mono-label text-[#495057]">· COVERAGE</div>
                  <div className="font-display text-xl">Warwickshire & surrounding areas</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — booking panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="border-2 border-[#0B1215] bg-[#0B1215] text-[#F8F9FA] p-8 sm:p-10 grain relative overflow-hidden">
              <div className="relative z-10">
                <div className="font-mono-label text-[#FF5A36] mb-6 flex items-center gap-2">
                  <Calendar size={14} /> · LIVE AVAILABILITY
                </div>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
                  Book a slot on<br />
                  <span className="italic font-light text-[#FF5A36]">my diary.</span>
                </h3>
                <p className="mt-6 text-[#F8F9FA]/75 leading-relaxed max-w-md">
                  Real-time availability. Pick your service, pick your slot, get an instant
                  confirmation email. No back-and-forth.
                </p>

                <ol className="mt-8 space-y-2 text-sm text-[#F8F9FA]/80 max-w-md">
                  <li>
                    <span className="text-[#FF5A36] font-bold">01</span> · Choose your service
                  </li>
                  <li>
                    <span className="text-[#FF5A36] font-bold">02</span> · Pick a live slot from my
                    calendar
                  </li>
                  <li>
                    <span className="text-[#FF5A36] font-bold">03</span> · Confirm — you'll get an
                    instant email
                  </li>
                </ol>

                <a
                  href={BOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-10 inline-flex items-center gap-3 bg-[#FF5A36] hover:bg-[#E04928] text-[#F8F9FA] px-8 py-5 font-mono-label active:scale-95 transition-all text-base"
                  data-testid="book-cta"
                >
                  BOOK NOW · OPEN THE DIARY <ArrowRight size={18} />
                </a>

                <p className="mt-6 text-xs text-[#F8F9FA]/50 leading-relaxed max-w-md">
                  Opens in a new window · powered by Calendly · your card isn't charged at booking.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
